/* eslint-disable import/no-cycle */
import { Button, VStack, Text, Stack } from '@chakra-ui/core';
import { AnimatePresence } from 'framer-motion';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdAdd } from 'react-icons/md';
import { MultiChoiceOption } from '../../../../questionnaireTemplate.types';
import { QuestionnaireTemplateStore } from '../../../../store/questionnaireTemplateModel';
import { OptionItem } from './OptionItem';

const OPTIONS_DEFAULT: MultiChoiceOption[] = [];
const SPACING_BETWEEN_ITEMS = 12;
const MIN_OPTIONS_COUNT = 2;
const MAX_OPTIONS_COUNT = 6;

const ADD_BUTTON_PROPS = {
  leftIcon: <MdAdd />,
  variant: 'ghost',
  size: 'sm',
  width: 'fit-content',
} as const;

export const OptionsManager = React.memo(function OptionsManager({ elementId }: { elementId: string }) {
  const { t } = useTranslation('questionnaireTemplate');

  const options = QuestionnaireTemplateStore.useStoreState(
    (state) => state.entities.elements[elementId]?.options || OPTIONS_DEFAULT,
  );

  const reorder = QuestionnaireTemplateStore.useStoreActions((a) => a.reorderOptions);
  const addOption = QuestionnaireTemplateStore.useStoreActions((a) => a.addOption);

  const containerRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  const isRemovable = useMemo(() => options.length > MIN_OPTIONS_COUNT, [options.length]);

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (activeIdx === null || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const children = Array.from(containerRef.current.children) as HTMLElement[];

      const relativeY = e.clientY - containerRect.top;

      let newIndex = activeIdx;
      let accumulatedHeight = 0;

      /* eslint-disable-next-line no-plusplus */
      for (let i = 0; i < children.length; i++) {
        const childHeight = children[i].offsetHeight;
        const itemMiddle = accumulatedHeight + childHeight / 2;

        /* istanbul ignore next */
        if (relativeY > itemMiddle) {
          newIndex = i;
        }
        accumulatedHeight += childHeight + SPACING_BETWEEN_ITEMS;
      }

      /* istanbul ignore next */
      if (newIndex !== activeIdx) {
        reorder({ elementId, oldIndex: activeIdx, newIndex });
        setActiveIdx(newIndex);
      }
    },
    [activeIdx, elementId, reorder],
  );

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    setActiveIdx(null);
    /* istanbul ignore next */
    if (containerRef.current) {
      containerRef.current.releasePointerCapture(e.pointerId);
    }
  }, []);

  const handleAddOption = useCallback(() => {
    addOption(elementId);
  }, [addOption, elementId]);

  const createDragStartHandler = useCallback(
    (index: number) => (e: React.PointerEvent) => {
      e.stopPropagation();
      setActiveIdx(index);
      /* istanbul ignore next */
      if (containerRef.current) {
        containerRef.current.setPointerCapture(e.pointerId);
      }
    },
    [],
  );

  const stackStyles = useMemo(
    () => ({
      spacing: 1,
      cursor: activeIdx !== null ? 'grabbing' : 'default',
    }),
    [activeIdx],
  );

  if (options.length === 0) return null;

  return (
    <VStack align="stretch" spacing={1}>
      <Text fontSize="sm" color="gray.600">
        {t('formBuilder.canvas.element.options.title')}
      </Text>
      <Stack
        {...stackStyles}
        ref={containerRef}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        data-testid="options-container"
      >
        <AnimatePresence initial={false}>
          {options.map((option, idx) => (
            <OptionItem
              key={option?.id}
              elementId={elementId}
              option={option}
              index={idx}
              isActive={activeIdx === idx}
              isAnyDragging={activeIdx !== null}
              isRemovable={isRemovable}
              onDragStart={createDragStartHandler(idx)}
            />
          ))}
        </AnimatePresence>
      </Stack>

      {options.length < MAX_OPTIONS_COUNT && (
        <Button {...ADD_BUTTON_PROPS} onClick={handleAddOption} tabIndex={-1}>
          <Text textDecoration="underline" fontWeight={400}>
            {t('formBuilder.canvas.element.options.addOption')}
          </Text>
        </Button>
      )}
    </VStack>
  );
});
